import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import { useRegisterMutation } from '../redux/services/user/userApi';
import { useGetCountriesQuery } from '../redux/services/data/countriesApi';
import { useGetCitiesQuery } from '../redux/services/data/citiesApi';

import { FormTemplate } from '../components/form/forms/FormTemplate';
import { ErrorMsg } from '../components/ui/ErrorMsg';
import { InputsTemplate } from '../components/form/inputs/InputsTemplate';
import { Loader } from '../components/ui/Loader';
import { Dialog } from '../components/ui/Dialog';

import {
  initialValues,
  mock,
  validationSchemaRegistration,
} from '../lib/mocks/registration-mock';
import { addDataForDepedentSelects, handleResponseError } from '../utils/data';
import { EMPTY_ARRAY, errorMessages } from '../data/constants';
import { handleResetSelectsRefs } from '../utils/ui';
import { selectsDependentNames } from '../lib/mocks/add-realty-mock';

export const Registration = () => {
  const [inputs, setInputs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegisterPopup, setIsRegisterPopup] = useState(false);

  const [selectedAgentCountryId, setSelectedAgentCountryId] = useState();
  const [activeSelect, setActiveSelect] = useState('');
  const [multiSelectRefs, setMultiSelectRefs] = useState({});

  const {
    data: countries = EMPTY_ARRAY,
    isLoading: isCountriesLoading,
    error: countriesError,
  } = useGetCountriesQuery();

  const [register, { isLoading: isRegistrationLoading }] =
    useRegisterMutation();

  // Result unused on purpose: `isSettingRedux` copies it into dashboardSlice, which the
  // city select reads. Don't remove until the mirror is gone (redux-mirror-cleanup.md).
  useGetCitiesQuery(
    {
      country_id: selectedAgentCountryId,
      isSettingRedux: true,
    },
    {
      skip: !selectedAgentCountryId,
      refetchOnMountOrArgChange: true,
    }
  );

  const handleResetLocationSelects = name => {
    setActiveSelect(name);
  };

  const handleCountrySelect = selectedItem => {
    setSelectedAgentCountryId(selectedItem?.value);
    handleResetLocationSelects(selectsDependentNames.agentCountryId);
  };

  const addDataForSelects = async props => {
    const updatedmock = await addDataForDepedentSelects({
      ...props,
      handleCountrySelect,
      mock,
    });

    setInputs(updatedmock);

    return;
  };

  const handleSubmit = async values => {
    setIsLoading(true);
    setError(null);

    const { agent_city, ...rest } = values;
    const response = await register({ ...rest, agent_city_id: agent_city });

    if (response.error) {
      setError(handleResponseError(response.error));
    } else {
      setIsRegisterPopup(true);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (activeSelect) {
      handleResetSelectsRefs(activeSelect, {
        ...multiSelectRefs,
      });
    }
  }, [activeSelect, selectedAgentCountryId]);

  useEffect(() => {
    if (!isCountriesLoading) {
      if (countries && countries.length > 0) {
        addDataForSelects({ countries });
      }

      if (countriesError) {
        setError(errorMessages.wentWrong);
      }

      setIsLoading(false);
    }
  }, [countries, isCountriesLoading]);

  return (
    <div className="flex flex-col items-center gap-5 pt-20 pb-12 pl-2 pr-2 w-full h-full bg-white md:pt-12 md:pr-5 md:pl-[11.5rem] lg:pl-[12.25rem]">
      <div>
        <h2 className="text-lg font-bold text-blackColor">Registration</h2>
      </div>

      {countries.length > 0 && inputs.length > 0 && !isLoading && (
        <FormTemplate
          initialValues={initialValues}
          validationSchema={Yup.object(validationSchemaRegistration)}
          handleSubmitForm={handleSubmit}
          buttonText={'Register'}
          bgColor={'bg-purpleColor border-purpleColor'}
          classes={'items-center'}
        >
          {formProps => (
            <InputsTemplate
              formProps={formProps}
              inputsList={inputs}
              setMultiSelectRefs={setMultiSelectRefs}
            />
          )}
        </FormTemplate>
      )}

      {isLoading && !error && (
        <div className="pt-6">
          <Loader
            height={40}
            width={40}
          />
        </div>
      )}

      {isRegisterPopup && (
        <Dialog
          content={
            <span className="text-blackColor text-sm font-medium">
              Registration is successful
            </span>
          }
          classes={'flex justify-center items-center w-80 h-24'}
          isOpen={isRegisterPopup}
          onClose={() => setIsRegisterPopup(false)}
        />
      )}

      {error !== null && <ErrorMsg message={error} />}
    </div>
  );
};
